/**
 * Dashboard — KPI deck + Giving-by-Class + New-Signups + Active-Campaigns +
 * Recent-Activity + product-analytics (Top Screens, App Usage Today), in the
 * navy-gold design.
 *
 * Every section is wired to LIVE Supabase data:
 *   - KPIs            → profiles / rsvps (scoped to the current hero event) /
 *                       analytics_daily / active campaign
 *   - Giving by Class → admin_giving_by_class  (donations ⋈ profiles.class_year)
 *   - New Signups     → admin_signups_daily     (profiles.created_at, daily)
 *   - Active Campaigns→ campaigns (is_active)
 *   - Recent Activity → admin_recent_activity    (signups/gifts/RSVPs/photos)
 *   - Top Screens     → analytics_screen_stats
 *   - App Usage Today → analytics_daily
 * The three admin_* views are admin-gated (is_admin()); non-admins get no rows.
 */
import React from 'react';
import { useList, useInvalidate } from '@refinedev/core';
import { Link, useNavigate } from 'react-router';
import { UsersRound, CalendarCheck, HandHeart, Activity, ShieldAlert, BarChart3, type AdminIcon } from '../components/icons';
import { brand, money, initials } from '../theme/brand';

interface Kpi {
  label: string;
  value: string;
  delta: string;
  up: boolean | null;
  icon: AdminIcon;
}
const KPIS: Kpi[] = [
  { label: 'Total Alumni', value: '…', delta: 'this week', up: true, icon: UsersRound },
  // 'Event RSVPs' is the fallback label — at render time it becomes
  // "<hero event title> RSVPs" (see the kpis map below).
  { label: 'Event RSVPs', value: '…', delta: 'this week', up: true, icon: CalendarCheck },
  { label: 'Annual Fund', value: '…', delta: 'of goal', up: true, icon: HandHeart },
  { label: 'Active Today', value: '…', delta: 'today', up: true, icon: Activity },
  { label: 'Pending Moderation', value: '…', delta: 'to review', up: false, icon: ShieldAlert },
];

interface DailyRow { day: string; events: number; active_users: number; sessions: number; screen_views: number; }
interface ScreenRow { screen: string; label: string; views: number; }
interface GivingRow { class_year: string; gifts: number; total: number; }
interface SignupRow { day: string; signups: number; }
interface ActivityRow {
  id: string;
  actor: string | null;
  kind: 'signup' | 'donation' | 'rsvp' | 'photo';
  detail: string | null;
  amount: number | null;
  occurred_at: string;
}
interface CampaignRow { id: string; title: string; raised: number; goal: number; is_active: boolean; }
interface ReportRow { id: string; status: string; }

/** "12m" / "3h" / "2d" relative-time, from an ISO timestamp. */
function timeAgo(iso: string): string {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s`;
  const m = s / 60; if (m < 60) return `${Math.floor(m)}m`;
  const h = m / 60; if (h < 24) return `${Math.floor(h)}h`;
  const d = h / 24; if (d < 7) return `${Math.floor(d)}d`;
  const w = d / 7; if (w < 5) return `${Math.floor(w)}w`;
  const mo = d / 30; if (mo < 12) return `${Math.floor(mo)}mo`;
  return `${Math.floor(d / 365)}y`;
}

/** The activity sentence rendered after the actor's name, per kind. */
function activityText(a: ActivityRow): string {
  switch (a.kind) {
    case 'signup': return 'joined the alumni network';
    case 'donation': return `gave ${money(Number(a.amount ?? 0))}${a.detail ? ` to ${a.detail}` : ''}`;
    case 'rsvp': return `RSVP’d to ${a.detail ?? 'an event'}`;
    case 'photo': return 'uploaded a photo';
    default: return 'had activity';
  }
}

const card: React.CSSProperties = {
  background: '#fff',
  border: '1px solid ' + brand.cardLine,
  borderRadius: 16,
  padding: 18,
  boxShadow: '0 1px 3px rgba(16,30,60,.04)',
};

function CardHead({ title, sub, action, to }: { title: string; sub?: string; action?: string; to?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div>
        <div style={{ fontFamily: brand.fDisplay, fontWeight: 700, fontSize: 15, color: brand.ink }}>{title}</div>
        {sub ? <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 11.5, color: brand.sub, marginTop: 2 }}>{sub}</div> : null}
      </div>
      {action && to ? (
        <Link to={to} style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 12, color: brand.gold, textDecoration: 'none' }}>
          {action}
        </Link>
      ) : null}
    </div>
  );
}

/** YYYY-MM-DD for `offset` days before today (UTC, matching the view's ::date). */
function dayKey(offset: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - offset);
  return d.toISOString().slice(0, 10);
}

export function Dashboard() {
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

  // ── KPI sources ────────────────────────────────────────────────────────────
  const { result: totalRes } = useList({ resource: 'profiles', pagination: { pageSize: 1 } });
  const { result: weekRes } = useList({
    resource: 'profiles',
    pagination: { pageSize: 1 },
    filters: [{ field: 'created_at', operator: 'gte', value: weekAgo }],
  });
  // The CURRENT HERO event — the same row the mobile Home hero shows: the
  // pinned (hero_featured) event first, else the latest by start_date. The KPI
  // below is labeled with its title and counts ITS RSVPs, not a global total.
  // LIVE via liveMode:'auto' (same bounded pageSize:1 opt-in rationale as the
  // RSVP counts below) so re-pinning the hero from the Events page relabels
  // this KPI — and re-scopes its counts — without a manual refresh.
  const { result: heroRes } = useList<{ id: string; title: string }>({
    resource: 'events',
    sorters: [
      { field: 'hero_featured', order: 'desc' },
      { field: 'start_date', order: 'desc' },
    ],
    pagination: { pageSize: 1 },
    liveMode: 'auto',
  });
  const heroEvent = heroRes?.data?.[0];
  // The hero-event RSVP KPI keeps its live count (intentional, see migration
  // 20260620... rsvps realtime). These are bounded pageSize:1 count reads, so
  // opting back into realtime here is cheap and safe — unlike the blanket
  // liveMode:'auto' we removed globally, which subscribed every list page.
  // Gated on the hero fetch so we never fire an unfiltered global count first.
  const heroFilter = heroEvent ? [{ field: 'event_id', operator: 'eq' as const, value: heroEvent.id }] : [];
  const { result: rsvpRes } = useList({
    resource: 'rsvps',
    pagination: { pageSize: 1 },
    filters: heroFilter,
    liveMode: 'auto',
    queryOptions: { enabled: heroRes != null },
  });
  const { result: rsvpWeekRes } = useList({
    resource: 'rsvps',
    pagination: { pageSize: 1 },
    filters: [...heroFilter, { field: 'created_at', operator: 'gte', value: weekAgo }],
    liveMode: 'auto',
    queryOptions: { enabled: heroRes != null },
  });
  // "Pending moderation" = reports not yet resolved (open or being reviewed).
  const { result: pendingRes } = useList<ReportRow>({
    resource: 'reports',
    pagination: { pageSize: 1 },
    filters: [{ field: 'status', operator: 'in', value: ['open', 'reviewing'] }],
  });
  // Class-year change requests awaiting a decision. Surfaced alongside pending
  // moderation so a queue nobody remembers to open still gets noticed.
  const { result: pendingClassYearRes } = useList({
    resource: 'class_year_change_requests',
    pagination: { pageSize: 1 },
    filters: [{ field: 'status', operator: 'eq', value: 'pending' }],
  });

  // ── Product analytics (today's usage + top screens) ──────────────────────────
  const { result: dailyRes } = useList<DailyRow>({
    resource: 'analytics_daily',
    pagination: { pageSize: 2 },
    sorters: [{ field: 'day', order: 'desc' }],
  });
  const { result: topRes } = useList<ScreenRow>({
    resource: 'analytics_screen_stats',
    pagination: { pageSize: 5 },
    sorters: [{ field: 'views', order: 'desc' }],
  });

  // ── Giving by Class / New Signups / Active Campaigns / Recent Activity ───────
  const { result: givingRes } = useList<GivingRow>({
    resource: 'admin_giving_by_class',
    pagination: { pageSize: 8 },
    sorters: [{ field: 'total', order: 'desc' }],
  });
  const { result: signupsRes } = useList<SignupRow>({
    resource: 'admin_signups_daily',
    pagination: { pageSize: 60 },
    sorters: [{ field: 'day', order: 'desc' }],
  });
  // LIVE opt-in (the global liveMode stays 'off' — see App.tsx). This is the
  // dashboard's giving signal: apply_cheddarup_donation bumps campaigns.raised
  // in the SAME transaction as the donation insert, so a Cheddar Up gift landing
  // from the webhook shows up here as a campaigns UPDATE. Subscribing to
  // `campaigns` (in the publication) rather than opening a second channel on
  // donations keeps this to one live hook and moves the Raised KPI and the
  // Active Campaigns bars together.
  const { result: campRes } = useList<CampaignRow>({
    resource: 'campaigns',
    pagination: { pageSize: 5 },
    filters: [{ field: 'is_active', operator: 'eq', value: true }],
    sorters: [{ field: 'raised', order: 'desc' }],
    liveMode: 'auto',
    // Giving by Class reads admin_giving_by_class, a VIEW — views are not in the
    // publication and can never emit, so it has to be invalidated rather than
    // subscribed. Same table+view pairing as donations/list.tsx.
    onLiveEvent: () => invalidate({ resource: 'admin_giving_by_class', invalidates: ['list'] }),
  });
  const { result: activityRes } = useList<ActivityRow>({
    resource: 'admin_recent_activity',
    pagination: { pageSize: 6 },
    sorters: [{ field: 'occurred_at', order: 'desc' }],
  });

  // ── Derived KPI values ───────────────────────────────────────────────────────
  const totalAlumni = totalRes?.total;
  const weeklyAlumni = weekRes?.total;
  const rsvpCount = rsvpRes?.total;
  const rsvpWeek = rsvpWeekRes?.total;
  const pendingMod = pendingRes?.total;
  const pendingClassYear = pendingClassYearRes?.total;
  const daily = dailyRes?.data ?? [];
  const todayActive = dailyRes ? Number(daily[0]?.active_users ?? 0) : undefined;
  const yestActive = Number(daily[1]?.active_users ?? 0);
  const activePct =
    todayActive != null && yestActive > 0 ? Math.round(((todayActive - yestActive) / yestActive) * 100) : null;
  const activeCampaign = campRes?.data?.[0];

  const kpis: Kpi[] = KPIS.map((k) => {
    if (k.label === 'Total Alumni')
      return {
        ...k,
        value: totalAlumni != null ? totalAlumni.toLocaleString() : '…',
        delta: weeklyAlumni != null ? `+${weeklyAlumni} this week` : k.delta,
        up: weeklyAlumni != null ? weeklyAlumni > 0 : k.up,
      };
    if (k.label === 'Event RSVPs')
      return {
        ...k,
        label: heroEvent ? `${heroEvent.title} RSVPs` : k.label,
        value: rsvpCount != null ? rsvpCount.toLocaleString() : '…',
        delta: rsvpWeek != null ? `+${rsvpWeek} this week` : k.delta,
        up: rsvpWeek != null ? rsvpWeek > 0 : k.up,
      };
    if (k.label === 'Annual Fund') {
      const r = activeCampaign ? Number(activeCampaign.raised) : null;
      const g = activeCampaign ? Number(activeCampaign.goal) : null;
      return {
        ...k,
        value: r != null ? `$${(r / 1000).toFixed(1)}K` : '…',
        delta: r != null && g ? `${Math.round((r / g) * 100)}% of goal` : k.delta,
        up: true,
      };
    }
    if (k.label === 'Active Today')
      return {
        ...k,
        value: todayActive != null ? todayActive.toLocaleString() : '…',
        delta: activePct != null ? `${activePct >= 0 ? '+' : ''}${activePct}% vs yesterday` : 'today',
        up: activePct != null ? activePct >= 0 : null,
      };
    if (k.label === 'Pending Moderation')
      return {
        ...k,
        value: pendingMod != null ? pendingMod.toLocaleString() : '…',
        delta:
          pendingMod != null
            ? pendingClassYear
              ? `${pendingMod} to review · ${pendingClassYear} class-year request${pendingClassYear === 1 ? '' : 's'}`
              : `${pendingMod} to review`
            : k.delta,
        up: pendingMod != null ? false : k.up,
      };
    return k;
  });

  // ── Giving by Class ──────────────────────────────────────────────────────────
  const giving = (givingRes?.data ?? []).map((r) => ({ y: r.class_year, v: Number(r.total) }));
  const maxG = Math.max(1, ...giving.map((d) => d.v));

  // ── New Signups (30-day series + delta vs prior 30) ──────────────────────────
  const signupByDay = new Map((signupsRes?.data ?? []).map((r) => [r.day, Number(r.signups)]));
  const series30 = Array.from({ length: 30 }, (_, i) => signupByDay.get(dayKey(29 - i)) ?? 0); // oldest→newest
  const total30 = series30.reduce((a, b) => a + b, 0);
  const prior30 = Array.from({ length: 30 }, (_, i) => signupByDay.get(dayKey(59 - i)) ?? 0).reduce((a, b) => a + b, 0);
  const signupDelta = prior30 > 0 ? Math.round(((total30 - prior30) / prior30) * 100) : null;
  const maxS = Math.max(1, ...series30);
  const pts = series30.map((v, i) => `${(i / (series30.length - 1)) * 100},${40 - (v / maxS) * 36}`).join(' ');

  // ── Active Campaigns ─────────────────────────────────────────────────────────
  const campaigns: [string, number, number][] = (campRes?.data ?? []).map((c) => [c.title, Number(c.raised), Number(c.goal)]);

  // ── Recent Activity ──────────────────────────────────────────────────────────
  const activity = activityRes?.data ?? [];

  const topScreens = (topRes?.data ?? []).filter((s) => Number(s.views) > 0);

  return (
    <div style={{ padding: '24px 28px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 20 }}>
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.label} style={card}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 11,
                    background: 'rgba(196,151,49,.12)',
                    border: '1px solid rgba(196,151,49,.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon size={20} color={brand.gold} />
                </div>
                {k.up !== null ? (
                  <span
                    style={{
                      fontFamily: brand.fSans,
                      fontWeight: 700,
                      fontSize: 11,
                      color: k.up ? '#1f7a4d' : '#a4452f',
                      background: k.up ? '#e3f3ea' : '#f6e4de',
                      padding: '3px 8px',
                      borderRadius: 999,
                    }}
                  >
                    {k.delta}
                  </span>
                ) : null}
              </div>
              <div style={{ fontFamily: brand.fDisplay, fontWeight: 800, fontSize: 30, color: brand.ink, marginTop: 14, lineHeight: 1 }}>
                {k.value}
              </div>
              <div style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 12, color: brand.sub, marginTop: 5 }}>
                {k.label}
                {k.up === null ? ' · ' + k.delta : ''}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16, marginBottom: 20 }}>
        <div style={card}>
          <CardHead title="Giving by Class" sub={activeCampaign?.title ?? 'Completed gifts'} />
          {giving.length > 0 ? (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, height: 170, marginTop: 18, padding: '0 4px' }}>
              {giving.map((d, i) => (
                <div key={d.y} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontFamily: brand.fSans, fontWeight: 700, fontSize: 10.5, color: brand.sub }}>{'$' + (d.v / 1000).toFixed(0) + 'K'}</div>
                  <div
                    style={{
                      width: '100%',
                      height: (d.v / maxG) * 120,
                      background: i < 3 ? brand.goldGrad : 'linear-gradient(180deg,#2a5fa3,#1e4e8c)',
                      borderRadius: '6px 6px 0 0',
                    }}
                  />
                  <div style={{ fontFamily: brand.fDisplay, fontWeight: 700, fontSize: 12, color: brand.ink }}>{d.y}</div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ height: 170, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: brand.fSans, color: brand.sub, fontSize: 13 }}>
              No completed gifts yet.
            </div>
          )}
        </div>

        <div style={card}>
          <CardHead title="New Signups" sub="Last 30 days" />
          <div style={{ marginTop: 18 }}>
            <div style={{ fontFamily: brand.fDisplay, fontWeight: 800, fontSize: 30, color: brand.ink, lineHeight: 1 }}>
              {signupsRes ? `+${total30}` : '…'}
            </div>
            <div style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 12, color: signupDelta != null && signupDelta < 0 ? '#a4452f' : '#1f7a4d', marginTop: 4 }}>
              {signupDelta != null ? `${signupDelta >= 0 ? '▲' : '▼'} ${Math.abs(signupDelta)}% vs prior 30 days` : 'vs prior 30 days'}
            </div>
            <svg viewBox="0 0 100 44" preserveAspectRatio="none" style={{ width: '100%', height: 110, marginTop: 14 }}>
              <polyline points={pts} fill="none" stroke={brand.gold} strokeWidth="1.6" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
              <polyline points={`0,44 ${pts} 100,44`} fill="rgba(196,151,49,.12)" stroke="none" />
            </svg>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.1fr', gap: 16 }}>
        <div style={card}>
          <CardHead title="Active Campaigns" sub="Progress to goal" />
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {campaigns.length > 0 ? (
              campaigns.map(([n, r, g]) => (
                <div key={n}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 13, color: brand.ink }}>{n}</span>
                    <span style={{ fontFamily: brand.fSans, fontWeight: 700, fontSize: 12, color: brand.gold }}>
                      {money(r)} / {money(g)}
                    </span>
                  </div>
                  <div style={{ height: 9, borderRadius: 999, background: brand.paper, overflow: 'hidden', border: '1px solid ' + brand.cardLine }}>
                    <div style={{ width: Math.min(100, (r / g) * 100) + '%', height: '100%', background: brand.goldGrad, borderRadius: 999 }} />
                  </div>
                </div>
              ))
            ) : (
              <span style={{ fontFamily: brand.fSans, color: brand.sub, fontSize: 13 }}>{campRes ? 'No active campaigns.' : '…'}</span>
            )}
          </div>
        </div>

        <div style={card}>
          <CardHead title="Recent Activity" sub="Across the app" action="View feed" to="/activity_feed" />
          <div style={{ marginTop: 8 }}>
            {activity.length > 0 ? (
              activity.map((a, i) => (
                <div
                  key={a.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '11px 0',
                    borderBottom: i < activity.length - 1 ? '1px solid ' + brand.cardLine : 'none',
                  }}
                >
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: '50%',
                      background: brand.jaguar,
                      color: brand.paper,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: brand.fDisplay,
                      fontWeight: 700,
                      fontSize: 11,
                      flexShrink: 0,
                      border: '1px solid ' + brand.gold,
                    }}
                  >
                    {initials(a.actor ?? '?')}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontFamily: brand.fSans, fontWeight: 700, fontSize: 13, color: brand.ink }}>{a.actor ?? 'A member'}</span>
                    <span style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 13, color: brand.sub }}> {activityText(a)}</span>
                  </div>
                  <span style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 11, color: brand.sub, flexShrink: 0 }}>{timeAgo(a.occurred_at)}</span>
                </div>
              ))
            ) : (
              <div style={{ padding: '16px 0', fontFamily: brand.fSans, color: brand.sub, fontSize: 13 }}>
                {activityRes ? 'No recent activity yet.' : '…'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Live product analytics (Stage 2) — top screens + today's usage */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 20 }}>
        <div style={card}>
          <CardHead title="Top Screens" sub="Most-visited (all time)" />
          <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {topScreens.length > 0 ? (
              topScreens.map((s) => {
                const max = Number(topScreens[0].views) || 1;
                return (
                  <div key={s.screen}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                      <span style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 13, color: brand.ink }}>{s.label}</span>
                      <span style={{ fontFamily: brand.fSans, fontWeight: 700, fontSize: 12, color: brand.gold }}>
                        {Number(s.views).toLocaleString()}
                      </span>
                    </div>
                    <div style={{ height: 8, borderRadius: 999, background: brand.paper, overflow: 'hidden', border: '1px solid ' + brand.cardLine }}>
                      <div style={{ width: Math.max(4, (Number(s.views) / max) * 100) + '%', height: '100%', background: brand.goldGrad, borderRadius: 999 }} />
                    </div>
                  </div>
                );
              })
            ) : (
              <span style={{ fontFamily: brand.fSans, color: brand.sub, fontSize: 13 }}>No screen views recorded yet.</span>
            )}
          </div>
        </div>

        <div style={card}>
          <CardHead title="App Usage Today" sub="From product analytics" />
          <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
            {[
              { label: 'Screen Views', value: daily[0]?.screen_views },
              { label: 'Active Users', value: daily[0]?.active_users },
              { label: 'Sessions', value: daily[0]?.sessions },
            ].map((m) => (
              <div key={m.label} style={{ textAlign: 'center', padding: '10px 0' }}>
                <div style={{ fontFamily: brand.fDisplay, fontWeight: 800, fontSize: 26, color: brand.ink, lineHeight: 1 }}>
                  {dailyRes ? Number(m.value ?? 0).toLocaleString() : '…'}
                </div>
                <div style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 11.5, color: brand.sub, marginTop: 6 }}>{m.label}</div>
              </div>
            ))}
          </div>
          <div
            onClick={() => navigate('/analytics/screens')}
            style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: brand.gold, fontFamily: brand.fSans, fontWeight: 600, fontSize: 12 }}
          >
            <BarChart3 size={15} color={brand.gold} /> Open Screen Analytics →
          </div>
        </div>
      </div>
    </div>
  );
}
